import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toolbar } from "primereact/toolbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { Badge } from "primereact/badge";
import { FilterMatchMode, FilterOperator } from "primereact/api";

import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { currencyBodyTemplate } from "../../appconfig/AppHelper";
import { UberCalcListColumns } from "../../appconfig/DatatableSetting";
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { getAllDetails, deleteData, updateData } from "../../service/ApiService";
import {
  LIST_UBER_CALCULATIONS,
  DELETE_UBER_CALCULATION,
  UBER_CALCULATION_STATUS,
} from "../../config/ApiConstant";

const emptyFilters = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  sin_number: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  mobile_no: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
};

const UberCalcList = () => {
  document.title = "GST Calc | Nagra Accounting";
  const navigate = useNavigate();
  const toast = useRef<any>(null);

  const [uberCalcList, setUberCalcList] = useState<any[]>([]);
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [filters, setFilters] = useState<any>(emptyFilters);
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date(new Date().getFullYear() - 1, 0, 1));
  const [statusModal, setStatusModal] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);

  useEffect(() => {
    getUberCalcList(String(new Date().getFullYear() - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getUberCalcList = async (year?: string) => {
    try {
      const slug = year ? `${LIST_UBER_CALCULATIONS}?year=${year}` : LIST_UBER_CALCULATIONS;
      const response = await getAllDetails(slug);
      setUberCalcList(response || []);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to load records",
      });
      setPageLoad(true);
    }
  };

  const onYearChange = (e: any) => {
    const date: Date | null = e.value;
    setSelectedYear(date);
    const yr = date ? String(date.getFullYear()) : undefined;
    getUberCalcList(yr);
  };

  const onGlobalFilterChange = (e: any) => {
    setGlobalFilter(e.target.value);
  };

  const clearAll = () => {
    const defaultYear = new Date(new Date().getFullYear() - 1, 0, 1);
    setGlobalFilter(null);
    setFilters(emptyFilters);
    setSelectedYear(defaultYear);
    getUberCalcList(String(defaultYear.getFullYear()));
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: `Are you sure you want to delete ${rowData.name} ${rowData.year} GST Calculation?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData.id),
    });
  };

  const handleDelete = async (id: any) => {
    try {
      const response = await deleteData(DELETE_UBER_CALCULATION, id);
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response?.message || "Record deleted successfully",
      });
      getUberCalcList(selectedYear ? String(selectedYear.getFullYear()) : String(new Date().getFullYear() - 1));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to delete record",
      });
    }
  };

  const leftToolbarTemplate = () => (
    <span className="block mr-2 p-input-icon-left">
      <i className="pi pi-search" />
      <InputText
        type="search"
        value={globalFilter || ""}
        onChange={onGlobalFilterChange}
        placeholder="Search..."
      />
    </span>
  );

  const rightToolbarTemplate = () => (
    <div className="flex align-items-center gap-2">
      <Calendar
        value={selectedYear}
        view="year"
        dateFormat="yy"
        placeholder="Filter by Year"
        showIcon
        showButtonBar
        onChange={onYearChange}
        style={{ width: "160px" }}
      />
      <Button
        className="common-primary-button"
        icon="pi pi-plus"
        label="Add New"
        onClick={() => navigate("/gst-calc/add")}
      />
      <Button
        icon="pi pi-filter-slash"
        label="Clear"
        onClick={clearAll}
        className="p-button-outlined common-p-outline-button"
      />
    </div>
  );

  const handleStatusChange = async () => {
    if (!selectedRow) return;
    setStatusLoading(true);
    try {
      const response = await updateData(UBER_CALCULATION_STATUS, selectedRow.id, { status: "Done" });
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response?.message || "Status updated successfully",
      });
      setStatusModal(false);
      setSelectedRow(null);
      getUberCalcList(selectedYear ? String(selectedYear.getFullYear()) : undefined);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to update status",
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const statusBodyTemplate = (rowData: any) => {
    const isDone = rowData?.status === "Done";
    return (
      <div className="flex flex-column align-items-center gap-1">
        <InputSwitch
          checked={isDone}
          disabled={isDone}
          onChange={() => {
            setSelectedRow(rowData);
            setStatusModal(true);
          }}
          tooltip={isDone ? "Already Done" : "Mark as Done"}
          tooltipOptions={{ position: "bottom" }}
        />
        <Badge
          value={rowData?.status || "Pending"}
          severity={isDone ? "success" : "warning"}
        />
      </div>
    );
  };

  const actionBodyTemplate = (rowData: any) =>
    rowData ? (
      <div className="actions flex gap-2">
        <Button
          icon="pi pi-eye"
          tooltip="View"
          className="p-button-info p-button-sm"
          tooltipOptions={{ position: "bottom" }}
          onClick={() => navigate("/gst-calc/view/" + btoa(String(rowData.id)))}
        />
        <Button
          icon="pi pi-pencil"
          tooltip="Edit"
          className="common-edit-btn"
          tooltipOptions={{ position: "bottom" }}
          disabled={rowData?.status === "Done"}
          onClick={() => navigate("/gst-calc/edit/" + btoa(String(rowData.id)))}
        />
        <Button
          icon="pi pi-trash"
          tooltip="Delete"
          className="p-button-danger p-button-sm"
          tooltipOptions={{ position: "bottom" }}
          disabled={rowData?.status === "Done"}
          onClick={() => confirmDelete(rowData)}
        />
      </div>
    ) : (
      "-"
    );

  return (
    <>
      <AppBreadcrumb
        currentMenu="GST Calc"
        pageName="GST Calc List"
        pageTitle="GST Calc List"
      />
      <ConfirmDialog />
      <Dialog
        header="Confirm Status Change"
        visible={statusModal}
        style={{ width: "400px" }}
        onHide={() => { setStatusModal(false); setSelectedRow(null); }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => { setStatusModal(false); setSelectedRow(null); }}
              disabled={statusLoading}
            />
            <Button
              label="Confirm"
              icon="pi pi-check"
              className="common-primary-button"
              onClick={handleStatusChange}
              loading={statusLoading}
            />
          </div>
        }
      >
        <p>
          Are you sure you want to mark <strong>{selectedRow?.name}</strong>'s GST Calculation status as <strong>Done</strong>?
        </p>
      </Dialog>
      <div className="grid crud-demo">
        <div className="col-12">
          <Card className="bg-white lg:p-2 crud-demo">
            <Toast ref={toast} />
            <Toolbar
              className="gap-3"
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            />
            {pageLoad ? (
              <DataTable
                value={uberCalcList}
                showGridlines
                dataKey="id"
                stripedRows
                globalFilter={globalFilter}
                filters={filters}
                onFilter={(e) => setFilters(e.filters)}
                responsiveLayout="scroll"
                rows={defaultRowOptions}
                paginator={uberCalcList.length > 0}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatble-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries + " Records"}
                emptyMessage="No Records Found"
                globalFilterFields={["name", "sin_number", "mobile_no"]}
                removableSort
              >
                {UberCalcListColumns.map((col) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  }
                  if (col.field === "status") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={statusBodyTemplate}
                      />
                    );
                  }
                  if (["taxable_income_line_101", "non_taxable_line_102", "gst_collected", "gst_itc", "total_net_after_fees"].includes(col.field)) {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={currencyBodyTemplate}
                      />
                    );
                  }
                  return (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      filter
                    />
                  );
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {UberCalcListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default UberCalcList;
